import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { FloatingSelectField } from '@/components/ui/floating-input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  FINISH_OPTIONS,
  FINISH_LABELS,
  normalizeFinish,
  type CardFinish,
} from '@/constants/cardFinishes';
import { cn } from '@/lib/utils';

export type FinishComboboxValue = {
  finish: CardFinish | '';
  finish_label: string | null;
};

interface FinishComboboxProps {
  value: FinishComboboxValue;
  onChange: (next: FinishComboboxValue) => void;
  disabled?: boolean;
}

/**
 * Combobox for the Finish field.
 * - Always anchors to one of the 5 canonical buckets (`finish`).
 * - Allows free-text "branches" (e.g. "Foil Etched") stored as `finish_label`.
 * - If typed text doesn't auto-map to a canonical bucket, asks the user to pick one.
 */
export function FinishCombobox({ value, onChange, disabled }: FinishComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Pending custom branch awaiting bucket assignment
  const [pendingCustom, setPendingCustom] = useState<string | null>(null);
  const [pendingBucket, setPendingBucket] = useState<CardFinish | ''>('');

  const display = value.finish_label || (value.finish ? FINISH_LABELS[value.finish as CardFinish] : '');

  const filteredCanonical = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FINISH_OPTIONS;
    return FINISH_OPTIONS.filter(opt => opt.label.toLowerCase().includes(q));
  }, [query]);

  const exactCanonicalMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return FINISH_OPTIONS.find(opt => opt.label.toLowerCase() === q) ?? null;
  }, [query]);

  const showUseCustom = query.trim().length > 0 && !exactCanonicalMatch;

  const pickCanonical = (finish: CardFinish) => {
    onChange({ finish, finish_label: null });
    setQuery('');
    setOpen(false);
  };

  const pickCustom = (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    const mapped = normalizeFinish(text);
    if (mapped) {
      onChange({ finish: mapped, finish_label: text });
      setQuery('');
      setOpen(false);
    } else {
      // Defer: ask user to pick a bucket
      setPendingCustom(text);
      setPendingBucket('');
    }
  };

  const confirmPendingBucket = () => {
    if (!pendingCustom || !pendingBucket) return;
    onChange({ finish: pendingBucket, finish_label: pendingCustom });
    setPendingCustom(null);
    setPendingBucket('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPendingCustom(null); setPendingBucket(''); } }}>
        <PopoverTrigger asChild disabled={disabled}>
          <button type="button" className="w-full text-left">
            <FloatingSelectField label="Finish" hasValue={Boolean(display)}>
              <div className="floating-trigger h-14 flex items-center justify-between px-3 rounded-lg border border-white/[0.08] bg-white/[0.03] cursor-pointer">
                <span className={cn("truncate text-sm", !display && "text-transparent")}>{display || ' '}</span>
                <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0 ml-2" />
              </div>
            </FloatingSelectField>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-chrome border-white/[0.08]" align="start">
          {pendingCustom ? (
            <div className="p-3 space-y-3">
              <div className="text-xs text-muted-foreground">
                "<span className="text-foreground font-medium">{pendingCustom}</span>" isn't one of our standard finishes. Which bucket is it closest to? Filters group by these.
              </div>
              <RadioGroup value={pendingBucket} onValueChange={(v) => setPendingBucket(v as CardFinish)}>
                {FINISH_OPTIONS.map(opt => (
                  <label key={opt.value} className="flex items-center gap-3 h-10 px-2 rounded-md hover:bg-white/[0.05] cursor-pointer">
                    <RadioGroupItem value={opt.value} id={`bucket-${opt.value}`} />
                    <Label htmlFor={`bucket-${opt.value}`} className="cursor-pointer text-sm font-normal">{opt.label}</Label>
                  </label>
                ))}
              </RadioGroup>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setPendingCustom(null); setPendingBucket(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!pendingBucket}
                  onClick={confirmPendingBucket}
                  className="text-xs px-3 py-1 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search or type a custom finish…"
                value={query}
                onValueChange={setQuery}
              />
              <CommandList>
                {filteredCanonical.length === 0 && !showUseCustom && <CommandEmpty>No finish found.</CommandEmpty>}
                {filteredCanonical.length > 0 && (
                  <CommandGroup>
                    {filteredCanonical.map(opt => {
                      const isSelected = value.finish === opt.value && !value.finish_label;
                      return (
                        <CommandItem
                          key={opt.value}
                          value={opt.value}
                          onSelect={() => pickCanonical(opt.value)}
                        >
                          <Check className={cn("w-4 h-4 mr-2", isSelected ? "opacity-100" : "opacity-0")} />
                          {opt.label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
                {showUseCustom && (
                  <CommandGroup heading="Custom">
                    <CommandItem value={`__custom__${query}`} onSelect={() => pickCustom(query)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Use "{query.trim()}"
                    </CommandItem>
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>

      {value.finish_label && value.finish && (
        <div className="text-[11px] text-muted-foreground pl-1">
          Filed under <span className="text-foreground/80">{FINISH_LABELS[value.finish as CardFinish]}</span> for filtering
        </div>
      )}
    </div>
  );
}
